/**
 * Data transformer to convert external API response format to internal Diff type
 * Handles mapping between external field names and internal structure
 */

import type { ExternalDiffResponse, ExternalEvent } from '@/types/externalDiffs';

// Internal Diff type (matching DiffsListTab.tsx interface)
export interface Diff {
  id: string;
  page_id: string;
  slug: string;
  v_before: string;
  v_after: string;
  asset_id?: string;
  hook?: string;
  description?: string;
  published?: boolean;
  created_at?: string;
  updated_at?: string;
  period?: string;
  delta?: number;
  viewer_url?: string;
  primary_cropped_url?: string;
  secondary_cropped_url?: string;
  primary_original_url?: string;
  secondary_original_url?: string;
  company_logo_url?: string;
  highlight?: boolean;
  qa_check?: string | null;
  page?: {
    id: string;
    subslug?: string;
    pricing_page_url?: string;
    company?: {
      id: string;
      slug: string;
      url?: string;
      logo_url?: string;
    };
  };
  changes: Array<{
    id: string;
    event_type?: string;
    category?: string;
    slug: string;
    v_before?: string;
    v_after?: string;
    description?: string;
    before_extracts?: any;
    after_extract?: any;
    verified?: boolean;
    partially_correct?: boolean;
    invalid?: boolean;  // Event marked as invalid
    asset_id?: string;
    tags?: string[];
    asset_ids?: string[];
    created_at?: string;
    updated_at?: string;
    viewer_url?: string; // Each event has its own viewer URL
    annotation?: any; // Full annotation object for marker editor
  }>;
  changesSummary?: Array<{
    id: string;
    event_type?: string;
    category?: string;
    verified?: boolean;
    partially_correct?: boolean;
    invalid?: boolean;
  }>;
}

/**
 * Transform external event to internal change format
 */
function transformEvent(event: ExternalEvent, slug: string, diffUrls?: { before: string; after: string }): Diff['changes'][0] {
  const annotation = event.annotation || event.annotations;

  // Handle new API format with images object
  const images = (event as any).images;
  let transformedAnnotation = annotation;

  if (images && !annotation) {
    // Transform new images format to old annotation format
    transformedAnnotation = {
      compare_viewer_url: (event as any).url,
      cloudinary_urls: {
        primary_original_url: diffUrls?.before || '',
        secondary_original_url: diffUrls?.after || '',
        primary_cropped_url: images.before_cropped,
        secondary_cropped_url: images.after_cropped,
      },
      before_annotated_urls: images.before_annotated ? [images.before_annotated] : undefined,
      after_annotated_urls: images.after_annotated ? [images.after_annotated] : undefined,
      before_marker: (event as any).before_marker,
      after_marker: (event as any).after_marker,
      viewport1: (event as any).viewport1,
      viewport2: (event as any).viewport2,
    };
  } else if (annotation) {
    // Ensure cloudinary_urls exists with fallback to diff-level URLs
    if (!annotation.cloudinary_urls && diffUrls) {
      transformedAnnotation = {
        ...annotation,
        cloudinary_urls: {
          primary_original_url: diffUrls.before,
          secondary_original_url: diffUrls.after,
          primary_cropped_url: annotation.before_cropped_urls?.[0] || diffUrls.before,
          secondary_cropped_url: annotation.after_cropped_urls?.[0] || diffUrls.after,
        },
      };
    }
  } else {
    // Fallback: pick up flat marker/viewport fields from event (e.g. from ui_annotation spread)
    const flatBefore = (event as any).before_marker;
    const flatAfter = (event as any).after_marker;
    const flatV1 = (event as any).viewport1;
    const flatV2 = (event as any).viewport2;
    const flatMarkers = (event as any).markers;
    if (flatBefore || flatAfter || flatV1 || flatV2 || flatMarkers) {
      transformedAnnotation = {
        compare_viewer_url: (event as any).url,
        before_marker: flatBefore,
        after_marker: flatAfter,
        viewport1: flatV1,
        viewport2: flatV2,
        markers: flatMarkers,
        ...(diffUrls && {
          cloudinary_urls: {
            primary_original_url: diffUrls.before,
            secondary_original_url: diffUrls.after,
            primary_cropped_url: diffUrls.before,
            secondary_cropped_url: diffUrls.after,
          },
        }),
      };
    }
  }

  // Log if event ID is missing from API response
  if (!event.id) {
    console.warn('[transformEvent] Event missing ID from API:', {
      slug,
      type: event.type,
      description: event.description,
    });
  }

  return {
    id: event.id,
    event_type: event.type,
    category: event.category,
    slug: event.slug || slug,
    v_before: event.v_before,
    v_after: event.v_after,
    description: event.description,
    before_extracts: event.before_extracts,
    after_extract: event.after_extract,
    verified: event.verified,
    partially_correct: event.partially_correct,
    invalid: event.invalid,
    asset_id: event.asset_id,
    tags: event.tags,
    asset_ids: event.asset_ids,
    created_at: event.created_at,
    updated_at: event.updated_at,
    viewer_url: transformedAnnotation?.compare_viewer_url || (event as any).url,
    annotation: transformedAnnotation,
  };
}

/**
 * Extract viewer URL from first event
 */
function extractViewerUrl(events: ExternalEvent[]): string | undefined {
  if (!events || events.length === 0) {
    return undefined;
  }

  const firstEvent = events[0];
  // Try annotation (singular) first, then annotations (plural) for backwards compatibility
  return firstEvent.annotation?.compare_viewer_url || firstEvent.annotations?.compare_viewer_url;
}

/**
 * Extract Cloudinary URLs from first event (for backward compatibility)
 */
function extractCloudinaryUrls(events: ExternalEvent[]): {
  primary_cropped_url?: string;
  secondary_cropped_url?: string;
  primary_original_url?: string;
  secondary_original_url?: string;
} {
  if (!events || events.length === 0) {
    return {};
  }

  const firstEvent = events[0];
  // Check both annotation (singular) and annotations (plural) for backwards compatibility
  const annotation = firstEvent.annotation || firstEvent.annotations;

  // Prioritize cloudinary_urls structure (has original URLs correctly mapped)
  const urls = annotation?.cloudinary_urls;
  if (urls) {
    return {
      // Use actual original URLs (without crop transformations)
      primary_original_url: urls.primary_original_url,
      secondary_original_url: urls.secondary_original_url,

      // Cropped URLs (with c_crop parameters)
      primary_cropped_url: urls.primary_cropped_url,
      secondary_cropped_url: urls.secondary_cropped_url,
    };
  }

  // Fall back to older annotation structure (only has cropped URLs)
  if (annotation?.before_cropped_urls && annotation?.after_cropped_urls) {
    return {
      primary_cropped_url: annotation.before_cropped_urls[0],
      secondary_cropped_url: annotation.after_cropped_urls[0],
      // No original URLs available in this older format
    };
  }

  return {};
}

/**
 * Create changes summary from events
 */
function createChangesSummary(events: ExternalEvent[]): Diff['changesSummary'] {
  return events.map(event => ({
    id: event.id,
    event_type: event.type,
    category: event.category,
    verified: event.verified || false,
    partially_correct: event.partially_correct || false,
    invalid: false, // Not provided by external API
  }));
}

/**
 * Transform external diff response to internal Diff format
 * @param externalDiff - Diff data from external API
 * @param companyLogo - Optional company logo URL (deprecated - now included in API response)
 */
export function transformExternalDiff(
  externalDiff: ExternalDiffResponse,
  companyLogo?: string
): Diff {
  const cloudinaryUrls = extractCloudinaryUrls(externalDiff.events);
  const viewerUrl = extractViewerUrl(externalDiff.events);

  // Handle new API format: before_url/after_url instead of primary_original_url/secondary_original_url
  const beforeUrl = (externalDiff as any).before_url || externalDiff.primary_original_url;
  const afterUrl = (externalDiff as any).after_url || externalDiff.secondary_original_url;
  const logoUrl = (externalDiff as any).logo_url || externalDiff.company_logo || companyLogo;

  // Pass diff URLs to event transformer
  const diffUrls = beforeUrl && afterUrl ? { before: beforeUrl, after: afterUrl } : undefined;

  // Generate a unique ID if API doesn't provide one
  const diffId = externalDiff.id || `${externalDiff.slug}_${externalDiff.period || externalDiff.before_version}_${externalDiff.after_version}`;

  return {
    id: diffId,
    page_id: externalDiff.page_id,
    slug: externalDiff.slug,
    v_before: externalDiff.before_version,
    v_after: externalDiff.after_version,
    asset_id: externalDiff.asset_id,
    hook: externalDiff.title,
    description: externalDiff.description,
    published: externalDiff.published,
    created_at: externalDiff.created_at,
    updated_at: externalDiff.updated_at,
    period: externalDiff.period,
    delta: externalDiff.delta,
    viewer_url: viewerUrl || externalDiff.viewer_url,
    highlight: externalDiff.highlight || false,
    qa_check: externalDiff.qa_check,

    // Original screenshot URLs from diff response (preferred)
    primary_original_url: beforeUrl || cloudinaryUrls.primary_original_url,
    secondary_original_url: afterUrl || cloudinaryUrls.secondary_original_url,

    // Cropped URLs from first event annotation
    primary_cropped_url: cloudinaryUrls.primary_cropped_url,
    secondary_cropped_url: cloudinaryUrls.secondary_cropped_url,

    // Company logo from API
    company_logo_url: logoUrl,

    // Page and company structure
    page: {
      id: externalDiff.page_id,
      company: {
        id: externalDiff.slug, // Use slug as ID since we don't have company ID
        slug: externalDiff.slug,
        logo_url: logoUrl,
      },
    },

    // Transform events to changes, passing diff URLs for fallback
    changes: externalDiff.events.map(event => transformEvent(event, externalDiff.slug, diffUrls)),

    // Create changes summary
    changesSummary: createChangesSummary(externalDiff.events),
  };
}

/**
 * Transform an array of external diffs to internal format
 */
export function transformExternalDiffs(
  externalDiffs: ExternalDiffResponse[],
  companyLogos?: Record<string, string>
): Diff[] {
  return externalDiffs.map(diff =>
    transformExternalDiff(diff, companyLogos?.[diff.slug])
  );
}

/**
 * Strip heavy annotation/extract data from diffs for list-view caching.
 * Keeps only the fields needed to render the list row and for navigation.
 * Call this before storing diffs in Redux when you don't need full event data.
 */
export function stripAnnotationsForList(diffs: Diff[]): Diff[] {
  return diffs.map(diff => ({
    ...diff,
    changes: diff.changes.map(change => ({
      id: change.id,
      event_type: change.event_type,
      category: change.category,
      slug: change.slug,
      description: change.description,
      verified: change.verified,
      partially_correct: change.partially_correct,
      invalid: change.invalid,
      tags: change.tags,
      // Drop: annotation, before_extracts, after_extract, asset_id, asset_ids,
      //        viewer_url, v_before, v_after, created_at, updated_at
    })),
  }));
}

/**
 * Validate that an external diff has the required structure
 */
export function validateExternalDiff(diff: any): diff is ExternalDiffResponse {
  if (!diff || typeof diff !== 'object') {
    return false;
  }

  const required = [
    'id',
    'page_id',
    'slug',
    'before_version',
    'after_version',
    'period',
    'title',
    'events',
  ];

  for (const field of required) {
    if (!(field in diff)) {
      console.error(`Invalid external diff: missing required field "${field}"`);
      return false;
    }
  }

  if (!Array.isArray(diff.events)) {
    console.error('Invalid external diff: events must be an array');
    return false;
  }

  return true;
}
